import { FIELD_GRID_CLASS, GridCells } from 'components/GridItemList';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Button } from 'components/ui/button';
import { useOpenBillingNotesModal } from 'pages/invoices/layout/views/general-tab/DetailsCard';
import EditableMatterClientReference from 'pages/matters/components/EditableMatterClientReference';
import { FormattedMessage } from 'react-intl';
import { InvoiceData } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { byCaseNumber, useCanEditBillingNotes } from '../invoiceSections';
import { ReadCard, VALUE_ACTION_CLASS } from './GeneralCard';

/**
 * The cases the invoice is issued for, by case number: each case's chip with its billing notes, its client and the
 * matter reference, whose pencil edits it on the case. The card has no Edit of its own.
 */
export function IssuedForCasesCard({ invoice }: { invoice: InvoiceData }) {
    const openBillingNotesModal = useOpenBillingNotesModal(invoice.id);
    const showsBillingNotes = useCanEditBillingNotes(invoice);
    const cases = (invoice.invoiceMatters ?? []).toSorted(byCaseNumber);

    const billingNotesActions = (invoiceMatter: (typeof cases)[number]) => (
        <>
            <Button
                variant="ghost"
                size="sm"
                className={VALUE_ACTION_CLASS}
                onClick={() =>
                    openBillingNotesModal({
                        billingNotes: invoiceMatter.matterBillingDetails.billingNotes ?? '',
                        matterId: invoiceMatter.matterId,
                    })
                }
            >
                <FormattedMessage id="invoices.general-tab.details.edit-case-billing-notes" />
            </Button>
            {invoiceMatter.clientParty.id !== invoice.clientParty.id ? (
                <Button
                    variant="ghost"
                    size="sm"
                    className={VALUE_ACTION_CLASS}
                    onClick={() =>
                        openBillingNotesModal({
                            billingNotes: invoiceMatter.clientParty.billingNotes ?? '',
                            partyId: invoiceMatter.clientParty.id,
                        })
                    }
                >
                    <FormattedMessage id="invoices.general-tab.details.edit-client-billing-notes" />
                </Button>
            ) : null}
        </>
    );

    return (
        <ReadCard title={<FormattedMessage id="invoices.general-tab.details.issued-for-cases" />}>
            {cases.map((invoiceMatter, index) => (
                <dl
                    key={invoiceMatter.matterId ?? index}
                    className={cn(FIELD_GRID_CLASS, 'm-0', index > 0 && 'border-divider border-t pt-3.5')}
                >
                    <GridCells
                        rows={[
                            {
                                label: t('matters.matter'),
                                // The actions in the value, beside the chip: the row's button sits at its far end.
                                content: (
                                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                        <RecordLink
                                            to={`/matter/${invoiceMatter.matterId}`}
                                            label={invoiceMatter.fullName}
                                        />
                                        {showsBillingNotes ? billingNotesActions(invoiceMatter) : null}
                                    </span>
                                ),
                                wide: 'always',
                            },
                            {
                                label: t('invoices.general-tab.details.client'),
                                content: <PartyName party={invoiceMatter.clientParty} isLink showPartyNumber />,
                            },
                            {
                                label: t('invoices.general-tab.details.matter-ref'),
                                content: (
                                    <EditableMatterClientReference
                                        matterId={invoiceMatter.matterId}
                                        matterClientReference={invoiceMatter.matterClientReference}
                                    />
                                ),
                            },
                        ]}
                    />
                </dl>
            ))}
        </ReadCard>
    );
}
